import { FormControl, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translate } from 'react-switch-lang';
import { Button, Card, CardBody, Col, Container, FormGroup, Row } from 'reactstrap';
import { useBackupDownload } from 'src/hooks/useBackupDownload';
import { useExport } from 'src/hooks/useExport';
import { useHttpClient } from 'src/security/hooks/axiosProvider';
import SweetAlert from 'sweetalert2';
import Breadcrumb from '../breadcrumb/index';

const ExportConfirmationModal = (props) => {
  const httpClient = useHttpClient(true);
  const { keycloak } = useKeycloak();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedProject, setSelectedProject] = useState(undefined);
  const [selectedActivities, setSelectedActivity] = useState([]);
  const [subactivities, setSubactivities] = useState([]);
  const [selectedSubActivities, setSelectedSubActivities] = useState([]);
  const [productions, setProductions] = useState([]);
  const [selectedProductions, setSelectedProduction] = useState([]);

  const { triggerExport } = useExport();
  const { downloadBackup, loading: backupLoading, error: backupError } = useBackupDownload();

  const onSubmit = async (data) => {
    if (selectedProject === undefined ) {
      SweetAlert.fire({
        icon: 'error',
        title: 'Ouuups ...',
        text: props.t('msg_enter_project'),
      });
      return;
    }
    if (data.project_start_date > data.project_end_date) {
      SweetAlert.fire({
        icon: 'error',
        title: 'Ouuups ...',
        text: props.t('startDate_greater_than_endDate'),
      });
      return;
    }
    if(selectedActivities.length === 0){
      SweetAlert.fire({
        icon: 'error',
        title: 'Ouuups ...',
        text: props.t('msg_enter_activity'),
      });
      return;
    }
    if(selectedSubActivities.length === 0){
      SweetAlert.fire({
        icon: 'error',
        title: 'Ouuups ...',
        text: props.t('msg_enter_sub_activity'),
      });
      return;
    }
    if(selectedProductions.length === 0){
      SweetAlert.fire({
        icon: 'error',
        title: 'Ouuups ...',
        text: props.t('msg_enter_production'),
      });
      return;
    }
    const formData = {
      project: selectedProject,
      project_start_date: data['project_start_date'],
      project_end_date: data['project_end_date'],
      activities: selectedActivities,
      subactivities: selectedSubActivities,
      productions: selectedProductions
    };

    try {
      await triggerExport(formData);
      SweetAlert.fire({
        icon: 'success',
        title: props.t('export_msg'),
      });
    } catch (error) {
      SweetAlert.fire({
        icon: 'error',
        title: props.t('err'),
      });
    }
  };

  const minDate = new Date('2014-01-01').toISOString().split('T')[0];

  const fetchProjects = async () => {
    const isSuperAdmin = keycloak.hasResourceRole('ROLE_AGRISED_ADMIN_SUPER') ?? false;
    const userId = keycloak.tokenParsed.sub;
    const response = await httpClient.current?.get(`/api/project/all`);
    if (isSuperAdmin) {
      setProjects(response.data);
    } else {
      const filteredProjects = response.data.filter((project) => project.administrator.id === userId);
      setProjects(filteredProjects);     
    }
  };

  const fetchActivities = async (projectId) => {
    if (!projectId) {
      setActivities([]);
      setSubactivities([]);
      setProductions([]);
      return;
    }
    const response = await httpClient.current?.get(`/api/activity/activities/${projectId}`);
    setActivities(response.data);
  };

  useEffect(() => {
    if (selectedProject) {
      if (selectedActivities.length > 0) {
        httpClient.current?.get(`/api/subactivity/subActivities/${selectedProject}/${selectedActivities}`).then((response) => {
          const subActivities = response.data.filter((subactivity) =>
            selectedActivities.includes(subactivity.activity.id)
          );
          setSubactivities(subActivities);
        });
      } else {
        setSubactivities([]);
        setProductions([]);
      }
    } else {
      setSubactivities([]);
      setProductions([]);
    }
    fetchProjects();
  }, [selectedActivities, selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      if (selectedSubActivities.length > 0) {
        httpClient.current?.get(`/api/production/productions/${selectedProject}/${selectedSubActivities}`).then((response) => {
          const productions = response.data.filter((production) =>
            selectedSubActivities.includes(production.subActivity.id)
          );
          setProductions(productions);
        });
      } else {
        setProductions([]);
        setSubactivities([]);
      }
    } else {
      setProductions([]);
      setSubactivities([]); 
    }
  }, [selectedSubActivities, selectedProject]);

  useEffect(() => {
    const selectedSubActivitesData = subactivities.filter((subActivity) =>
      selectedSubActivities.includes(subActivity.id)
    );
    const subActivitesData = selectedSubActivitesData.filter((subActivity) =>
      selectedActivities.includes(subActivity.activity.id)
    );
    const subActivitesId = subActivitesData.map((subActivity) => subActivity.id);
    setSelectedSubActivities(subActivitesId);
  }, [selectedActivities]);

  useEffect(() => {
    const selectedProductionsData = productions.filter((production) => selectedProductions.includes(production.id));
    const productionsData = selectedProductionsData.filter((production) =>
      selectedSubActivities.includes(production.subActivity.id)
    );
    const productionsId = productionsData.map((production) => production.id);
    setSelectedProduction(productionsId);
  }, [selectedSubActivities]);

  useEffect(() => {
    if (!selectedProject) {
      setSelectedActivity([]);
      setSelectedSubActivities([]);
      setSelectedProduction([]);
    } else {
      fetchActivities(selectedProject);
    }
  }, [selectedProject]);

  return (
    <div>
      <Fragment>
        <Breadcrumb parent={props.t('export')} url={'export'} />
        <Container fluid={true}>
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <form className="needs-validation" noValidate onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup key="formgroup1" className="form-row">
                      <label htmlFor={'project_name'}>
                        {props.t('Project_Name')} <span>*</span>
                      </label>
                      <select
                        name="project_name"
                        id="project_name"
                        className="form-control digit"
                        onChange={(event) => {
                          setSelectedProject(event.target.value);
                          fetchActivities(event.target.value)
                        }}
                      >
                        <option value="">{props.t('select_project')}</option>
                        {projects.map((project) => (
                          <option id={project.id} key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                      <span>{errors.project_name && <p>{props.t('errors_champ')}</p>}</span>
                    </FormGroup>
                    <div key="div1" className="form-row">
                      <FormGroup key="formgroup2" className="col-md-6">
                        <label key="label2 " htmlFor="project_start_date">
                          {props.t('Start_Date')} <span>*</span>
                        </label>
                        <input
                          key="input2"
                          type="date"
                          className="form-control digit"
                          id="project_start_date"
                          min={minDate}
                          {...register('project_start_date', { required: true })}
                        />
                        <span>{errors.project_start_date && <p>{props.t('errors_champ')}</p>}</span>
                      </FormGroup>
                      <FormGroup key="formgroup3" className="col-md-6">
                        <label key="label3 " htmlFor="project_end_date">
                          {props.t('End_Date')} <span>*</span>
                        </label>
                        <input
                          key="input3"
                          type="date"
                          className="form-control digit"
                          id="project_end_date"
                          min={minDate}
                          {...register('project_end_date', { required: true })}
                        />
                        <span>{errors.project_end_date && <p>{props.t('errors_champ')}</p>}</span>
                      </FormGroup>
                    </div>
                    {activities.length > 0 && (
                      <FormControl sx={{ width: 1050 }}>
                        <InputLabel>{props.t('activities')}</InputLabel>
                        <Select
                          multiple
                          name="activity"
                          id="activity"
                          value={selectedActivities}
                          onChange={(event) => {
                            if (event.target.value.includes('selectAll')) {
                              setSelectedActivity(activities.map((activity) => activity.id));
                            } else {
                              setSelectedActivity(event.target.value);
                            }
                          }}
                          input={<OutlinedInput label="activity" />}
                        >
                          <MenuItem value="selectAll">{props.t('select_all')}</MenuItem>
                          {activities.map((activity) => (
                            <MenuItem key={activity.id} value={activity.id}>
                              {activity.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {selectedActivities.length > 0 && (
                      <FormControl sx={{ mt: 2, width: 1050 }}>
                        <InputLabel>{props.t('Sub_Activity')}</InputLabel>
                        <Select
                          multiple
                          name="sub_activity"
                          id="sub_activity"
                          value={selectedSubActivities}
                          onChange={(event) => {
                            if (event.target.value.includes('selectAll')) {                        
                              setSelectedSubActivities(subactivities.map((subActivity) => subActivity.id));
                            } else {
                              setSelectedSubActivities(event.target.value);
                            }
                          }}
                          input={<OutlinedInput label="sub_activity" />}
                        >
                          <MenuItem value="selectAll">{props.t('select_all')}</MenuItem>
                          {subactivities.map((subActivity) => (
                            <MenuItem key={subActivity.id} value={subActivity.id}>
                              {subActivity.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {selectedSubActivities.length > 0 && (
                      <FormControl sx={{ mt: 2, width: 1050 }}>
                        <InputLabel> {props.t('Productivity')}</InputLabel>
                        <Select
                          multiple
                          name="production"
                          id="production"
                          value={selectedProductions}
                          onChange={(event) => {
                            if (event.target.value.includes('selectAll')) {
                              setSelectedProduction(productions.map((production) => production.id));
                            } else {
                              setSelectedProduction(event.target.value);
                            }
                          }}
                          input={<OutlinedInput label="activity" />}
                        >
                          <MenuItem value="selectAll">{props.t('select_all')}</MenuItem>
                          {productions.map((production) => (
                            <MenuItem key={production.id} value={production.id}>
                              {production.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Button outline color="success" className="float-right mt-2" type="submit">
                      {props.t('extract')}
                    </Button>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <Card>
                <CardBody>
                  <h5>{props.t('backup_title')}</h5>
                  <p className="text-muted">{props.t('backup_description')}</p>
                  <Button
                    outline
                    color="success"
                    onClick={downloadBackup}
                    disabled={backupLoading}
                  >
                    {backupLoading ? props.t('backup_loading') : props.t('backup_download')}
                  </Button>
                  {backupError && <p className="text-danger mt-2">{backupError}</p>}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    </div>
  );
};

export default translate(ExportConfirmationModal);
